import React, { useState, useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, InputGroup, Alert, Spinner } from 'reactstrap';
import ChatMessageList from '../ChatMessageList';
import PromptSuggestions from '../PromptSuggestions';
import {
  useBootstrapBreakpoint,
  useIsMobile,
  getResponsiveModalProps,
  getResponsiveButtonProps,
  getResponsiveInputProps
} from '../../utils/mobileUtils';

const ChatModal = ({
  isOpen = false,
  toggle = () => {},
  messages = [],
  onSend = () => {},
  title = 'Chat',
  currentSessionId,
  isLoading = false,
  error = null,
  onScore, // Add onScore prop
  footerText,
  suggestions = [], // Add suggestions prop
  showSuggestionsWhenEmpty = true, // Show suggestions when no messages
  suggestionsTitle = 'Suggested prompts',
  maxSuggestions = null
}) => {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const isMobile = useIsMobile();
  const breakpoint = useBootstrapBreakpoint();

  // Get responsive props for Reactstrap components
  const modalProps = getResponsiveModalProps(breakpoint);
  const buttonProps = getResponsiveButtonProps(breakpoint);
  const inputProps = getResponsiveInputProps(breakpoint);

  // Create default welcome message when no messages exist
  const defaultMessage = {
    id: 'welcome-message',
    type: 'ai',
    content:
      "Hi, I'm your Wellness Buddy, an AI-powered virtual assistant that's been trained on FRN's articles, Summits, expert interviews, books and courses. I'm here to help you thrive.\n\nHow can I support you today?",
    timestamp: new Date().toISOString()
  };

  // Use messages with default welcome message if no messages exist
  const displayMessages = messages && messages.length > 0 ? messages : [defaultMessage];

  useEffect(() => {
    if (isOpen) {
      // focus the input when modal opens
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isMobile, isOpen]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    setText(''); // Clear input immediately
    onSend(trimmed);
  };

  const handleSuggestionClick = suggestionText => {
    if (isLoading) return;
    onSend(suggestionText);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="modal-virtual-mentor-chat" {...modalProps}>
      <ModalBody className={isMobile ? 'flex-fill overflow-auto' : ''} style={{ overflowY: 'auto' }}>
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}
        <ChatMessageList messages={displayMessages} onScore={onScore} />

        {/* Show suggestions when there are no user messages or when specified */}
        {suggestions && suggestions.length > 0 && showSuggestionsWhenEmpty && (!messages || messages.length === 0) && (
          <PromptSuggestions
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            title={suggestionsTitle}
            disabled={isLoading}
            maxVisible={maxSuggestions}
          />
        )}

        {isLoading && (
          <div className="d-flex align-items-center mt-2">
            <Spinner size="sm" className="me-2" />
            <span className="text-muted">Thinking...</span>
          </div>
        )}
      </ModalBody>

      {/* Show suggestions in footer when there are messages and suggestions should always be visible */}
      {suggestions && suggestions.length > 0 && !showSuggestionsWhenEmpty && messages && messages.length > 0 && (
        <div className="border-top">
          <PromptSuggestions
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            title={suggestionsTitle}
            disabled={isLoading}
            maxVisible={maxSuggestions}
            layout="grid"
          />
        </div>
      )}

      <ModalFooter className={`${isMobile ? 'flex-column border-top' : 'd-flex'} w-100`}>
        <InputGroup className="flex-grow-1" {...(inputProps.size && { size: inputProps.size })}>
          <Input
            innerRef={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={isLoading}
            style={inputProps.style}
          />
          <div className="input-group-append">
            <Button
              color="primary"
              onClick={handleSend}
              disabled={isLoading || !text.trim()}
              {...buttonProps}
              style={{ minWidth: isMobile ? '80px' : 'auto' }}
            >
              {isLoading ? <Spinner size="sm" /> : 'Send'}
            </Button>
          </div>
        </InputGroup>
        {footerText && (
          <div className={`text-muted${isMobile ? ' text-center mt-2' : ''}`} style={{ fontSize: '80%' }}>
            {footerText}
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ChatModal;
